<script setup>
import { computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useFunctionGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

import SettingsLayout from '../../SettingsLayout.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import StudioNav from './components/StudioNav.vue';
import { findNavItem } from './components/studioNav';
import { useAttachedInboxes } from './components/useAttachedInboxes';
import InboxPublicLink from 'dashboard/components-next/Inbox/InboxPublicLink.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();

const botId = computed(() => route.params.botId);
const bot = useFunctionGetter('agentBots/getBot', botId);
const inboxes = computed(() => store.getters['inboxes/getInboxes']);
const attachedInboxes = useAttachedInboxes(botId);

const isActive = computed(() => attachedInboxes.value.length > 0);
const activeNavItem = computed(() => findNavItem(route.name));
const navParams = computed(() => ({
  accountId: route.params.accountId,
  botId: botId.value,
}));

const botConfig = computed(() => bot.value.bot_config || {});

const modelFact = computed(() => t('AI_AGENT.ENGINE_NAME'));

const intentFact = computed(() => {
  const intent = botConfig.value.intent || {};
  if (!intent.enabled) return t('AI_AGENT.STUDIO.HERO.INTENT_OFF');
  return intent.mode === 'live'
    ? t('AI_AGENT.INTENT.FIELDS.MODE_OPTIONS.LIVE')
    : t('AI_AGENT.INTENT.FIELDS.MODE_OPTIONS.SHADOW');
});

const facts = computed(() => [
  {
    key: 'model',
    icon: 'i-lucide-sparkles',
    label: t('AI_AGENT.STUDIO.HERO.MODEL'),
    value: modelFact.value || t('AI_AGENT.STUDIO.HERO.NOT_SET'),
  },
  {
    key: 'inboxes',
    icon: 'i-lucide-inbox',
    label: t('AI_AGENT.STUDIO.HERO.INBOXES'),
    value: String(attachedInboxes.value.length),
  },
  {
    key: 'intent',
    icon: 'i-lucide-brain',
    label: t('AI_AGENT.STUDIO.HERO.INTENT'),
    value: intentFact.value,
  },
]);

const isOnPlayground = computed(
  () => activeNavItem.value.routeName === 'ai_agents_studio_playground'
);

const openPlayground = () => {
  router.push({ name: 'ai_agents_studio_playground', params: navParams.value });
};

const openInboxes = () => {
  router.push({
    name: 'settings_inbox_list',
    params: { accountId: route.params.accountId },
  });
};

const fetchBot = async () => {
  if (!bot.value.id) {
    try {
      await store.dispatch('agentBots/show', botId.value);
    } catch (error) {
      router.replace({ name: 'ai_agents_list' });
    }
  }
};

const fetchInboxBots = async () => {
  await store.dispatch('inboxes/get');
  inboxes.value.forEach(inbox => {
    store.dispatch('agentBots/fetchAgentBotInbox', inbox.id);
  });
};

watch(bot, value => {
  if (value.id && value.bot_type && value.bot_type !== 'aegis') {
    useAlert(t('AI_AGENT.STUDIO.NOT_AI_AGENT'));
    router.replace({ name: 'ai_agents_list' });
  }
});

onMounted(() => {
  store.dispatch('agentBots/get');
  fetchBot();
  fetchInboxBots();
});
</script>

<template>
  <SettingsLayout :is-loading="!bot.id">
    <template #header>
      <header
        class="relative flex flex-col gap-5 p-5 overflow-hidden rounded-2xl bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-container"
      >
        <div
          class="absolute rounded-full pointer-events-none -top-28 -right-20 size-72 bg-n-brand/10 blur-3xl"
          aria-hidden="true"
        />
        <router-link
          :to="{ name: 'ai_agents_list' }"
          class="relative flex items-center self-start gap-1 rounded-md text-label-small text-n-slate-11 transition-colors hover:text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
        >
          <span class="i-lucide-chevron-left size-3.5" />
          {{ $t('AI_AGENT.LIST.TITLE') }}
        </router-link>

        <div
          class="relative flex flex-col gap-4 md:flex-row md:items-center md:justify-between"
        >
          <div class="flex items-center min-w-0 gap-4">
            <div
              class="p-0.5 rounded-full shrink-0 bg-gradient-to-br from-n-brand via-n-iris-9 to-n-teal-9"
            >
              <div class="p-0.5 rounded-full bg-n-solid-2">
                <Avatar
                  :name="bot.name || ''"
                  :src="bot.thumbnail"
                  :size="56"
                  rounded-full
                />
              </div>
            </div>
            <div class="flex flex-col min-w-0 gap-1">
              <div class="flex flex-wrap items-center gap-2">
                <h1 class="truncate text-heading-1 text-n-slate-12">
                  {{ bot.name }}
                </h1>
                <span
                  class="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-label-small"
                  :class="
                    isActive
                      ? 'bg-n-teal-9/10 text-n-teal-11'
                      : 'bg-n-amber-9/10 text-n-amber-11'
                  "
                  data-testid="studio-status"
                >
                  <span
                    class="rounded-full size-1.5"
                    :class="isActive ? 'bg-n-teal-9' : 'bg-n-amber-9'"
                  />
                  {{
                    isActive
                      ? $t('AI_AGENT.STUDIO.STATUS.ACTIVE')
                      : $t('AI_AGENT.STUDIO.STATUS.INACTIVE')
                  }}
                </span>
              </div>
              <p class="truncate text-body-main text-n-slate-11">
                {{ bot.description || $t('AI_AGENT.STUDIO.HERO.NO_DESCRIPTION') }}
              </p>
            </div>
          </div>
          <Button
            v-if="!isOnPlayground"
            icon="i-lucide-flask-conical"
            size="sm"
            :label="$t('AI_AGENT.STUDIO.HERO.OPEN_PLAYGROUND')"
            class="self-start md:self-center"
            @click="openPlayground"
          />
        </div>

        <dl class="relative flex flex-wrap gap-2">
          <div
            v-for="fact in facts"
            :key="fact.key"
            class="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-n-alpha-1 outline outline-1 -outline-offset-1 outline-n-weak"
          >
            <span :class="fact.icon" class="size-3.5 text-n-slate-10" />
            <dt class="text-label-small text-n-slate-10">{{ fact.label }}</dt>
            <dd class="text-label-small text-n-slate-12">{{ fact.value }}</dd>
          </div>
        </dl>

        <div
          v-if="attachedInboxes.length"
          class="relative flex flex-col gap-2"
          data-testid="studio-live-inboxes"
        >
          <span class="text-label-small text-n-slate-10">
            {{ $t('AI_AGENT.STUDIO.HERO.LIVE_ON') }}
          </span>
          <div
            v-for="inbox in attachedInboxes"
            :key="inbox.id"
            class="flex flex-wrap items-center justify-between gap-2 px-3 py-2 rounded-lg bg-n-alpha-1 outline outline-1 -outline-offset-1 outline-n-weak"
          >
            <span class="flex items-center gap-2 min-w-0">
              <ChannelIcon class="size-4 text-n-slate-10" :inbox="inbox" />
              <span class="truncate text-body-main text-n-slate-12">
                {{ inbox.name }}
              </span>
            </span>
            <InboxPublicLink :inbox="inbox" />
          </div>
        </div>

        <div
          v-if="!isActive"
          class="relative flex flex-col gap-3 p-3 rounded-xl bg-n-amber-9/10 sm:flex-row sm:items-center sm:justify-between"
          data-testid="studio-inactive-hint"
        >
          <div class="flex items-start gap-2">
            <span
              class="i-lucide-plug-zap size-4 mt-0.5 shrink-0 text-n-amber-11"
            />
            <div class="flex flex-col">
              <span class="text-heading-3 text-n-slate-12">
                {{ $t('AI_AGENT.STUDIO.HERO.INACTIVE_TITLE') }}
              </span>
              <span class="text-body-main text-n-slate-11">
                {{ $t('AI_AGENT.STUDIO.HERO.INACTIVE_HINT') }}
              </span>
            </div>
          </div>
          <Button
            size="sm"
            color="amber"
            variant="outline"
            icon="i-lucide-arrow-up-right"
            trailing-icon
            :label="$t('AI_AGENT.STUDIO.HERO.GO_TO_INBOXES')"
            class="self-start shrink-0 sm:self-center"
            @click="openInboxes"
          />
        </div>
      </header>
    </template>
    <template #body>
      <div class="flex flex-col gap-6 pt-2 lg:flex-row lg:items-start">
        <aside class="lg:sticky lg:top-4 lg:w-52 shrink-0">
          <StudioNav :active-key="activeNavItem.key" :params="navParams" />
        </aside>
        <div class="flex flex-col flex-1 min-w-0 gap-5">
          <div class="flex flex-col gap-1">
            <h2 class="text-heading-2 text-n-slate-12">
              {{ $t(`AI_AGENT.STUDIO.TABS.${activeNavItem.key}`) }}
            </h2>
            <p class="text-body-main text-n-slate-11">
              {{ $t(`AI_AGENT.STUDIO.TAB_DESCRIPTIONS.${activeNavItem.key}`) }}
            </p>
          </div>
          <router-view v-if="bot.id" :bot="bot" />
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
